import { useMemo, useState } from 'preact/hooks';
import {
  attendusFor,
  groupAttendus,
  searchAttendus,
  type Attendu,
} from '../../lib/referentiel.ts';
import type { Level } from '../../lib/levels.ts';
import { closeModal } from '../../ui/ui-store.ts';

interface Props {
  subject: string;
  level: Level;
  /** Ids already on the lesson when the picker opened. */
  initial: readonly string[];
  onConfirm: (ids: string[]) => void;
}

/**
 * Pick attendus from the référentiel, in a dialog of its own.
 *
 * The list runs to 178 entries for français, so it needs the whole dialog: a
 * search that stays put, headings that stick while their block scrolls past,
 * and rows big enough to hit with a finger. Squeezed inside the lesson form it
 * was a scrolling box inside a scrolling box, which is exactly as bad as it
 * sounds on a tablet.
 *
 * The selection is local until "Valider": closing or pressing Escape leaves
 * the lesson exactly as it was.
 */
export function CompetencePickerModal({ subject, level, initial, onConfirm }: Props) {
  const [chosen, setChosen] = useState<string[]>([...initial]);
  const [query, setQuery] = useState('');
  const [onlyChosen, setOnlyChosen] = useState(false);

  const available = useMemo(() => attendusFor(subject, level), [subject, level]);
  const selected = useMemo(() => new Set(chosen), [chosen]);

  const visible = useMemo(() => {
    const found = searchAttendus(available, query);
    return onlyChosen ? found.filter((a) => selected.has(a.id)) : found;
  }, [available, query, onlyChosen, selected]);

  const groups = useMemo(() => groupAttendus(visible), [visible]);

  function toggle(attendu: Attendu) {
    setChosen((current) =>
      current.includes(attendu.id)
        ? current.filter((id) => id !== attendu.id)
        : [...current, attendu.id],
    );
  }

  function confirm() {
    onConfirm(chosen);
    closeModal();
  }

  return (
    <div class="picker">
      <div class="pickerTools">
        <input
          id="competenceSearch"
          class="searchBox"
          type="search"
          value={query}
          placeholder={`Filtrer les ${available.length} attendus…`}
          onInput={(e) => setQuery(e.currentTarget.value)}
        />
        <label class="pickerFilter">
          <input
            type="checkbox"
            checked={onlyChosen}
            onChange={(e) => setOnlyChosen(e.currentTarget.checked)}
          />
          <span>Sélection ({chosen.length})</span>
        </label>
      </div>

      <div class="pickerList">
        {groups.length === 0 && (
          <p class="empty">
            {onlyChosen && chosen.length === 0
              ? 'Aucun attendu sélectionné pour l’instant.'
              : 'Aucun attendu ne correspond.'}
          </p>
        )}

        {groups.map((group, index) => (
          <section class="pickerGroup" key={`${group.section}|${group.parent}|${index}`}>
            {group.section && <h3 class="pickerSection">{group.section}</h3>}
            {group.parent && <p class="pickerParent">{group.parent}</p>}
            {group.attendus.map((attendu) => (
              <label
                class={selected.has(attendu.id) ? 'pickerRow chosen' : 'pickerRow'}
                key={attendu.id}
              >
                <input
                  type="checkbox"
                  checked={selected.has(attendu.id)}
                  onChange={() => toggle(attendu)}
                />
                <span class="pickerText">
                  {attendu.text}
                  {attendu.kind && <span class="pickerKind">{attendu.kind}</span>}
                </span>
              </label>
            ))}
          </section>
        ))}
      </div>

      <div class="pickerFoot">
        <span class="small muted">
          {chosen.length === 0
            ? 'Aucun attendu sélectionné'
            : `${chosen.length} attendu${chosen.length > 1 ? 's' : ''} sélectionné${chosen.length > 1 ? 's' : ''}`}
        </span>
        <span class="spacer" />
        <button class="btn" onClick={closeModal}>
          Annuler
        </button>
        <button class="btn primary" onClick={confirm}>
          Valider
        </button>
      </div>
    </div>
  );
}
